import { Modal, Button, Card, Tabs, Tab, Badge, Table } from "react-bootstrap";
import { useEffect, useState } from "react";
import moment from "moment";
import {
  FaRoute,
  FaMapMarkerAlt,
  FaClock,
  FaRoad,
  FaEdit,
  FaTruck,
  FaCalendar,
  FaUser,
} from "react-icons/fa";
import TableComponent from "../TableComponent";
import { useAuth } from "../../hooks/useAuth";

function RouteDetailsModal({ show, onHide, initRoute }) {
  const { backendUrl } = useAuth();
  const [route, setRoute] = useState(initRoute);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const getRouteDetails = async () => {
      const response = await fetch(
        `${backendUrl}/api/fleet/routes/${initRoute?.route_id}`
      );
      if (!response.ok) {
        // I don't know what to do
      } else {
        let resp = await response.json();
        setRoute(resp.data);
      }
    };
    if (initRoute) getRouteDetails();
  }, [initRoute, backendUrl]);

  if (!route) return null;

  // Helper function to get status badge
  const getStatusBadge = (isActive) => {
    return isActive ? "success" : "secondary";
  };

  // Helper function to get trip status badge
  const getTripStatusBadge = (status) => {
    const statusMap = {
      planned: "info",
      in_progress: "primary",
      completed: "success",
      cancelled: "danger",
      delayed: "warning",
    };
    return statusMap[status] || "secondary";
  };

  // Overview Tab Content
  const renderOverviewTab = () => (
    <div>
      {/* Route Header Card */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-3 align-items-center">
            <FaRoute size={24} className="text-primary" />
            <div>
              <h5 className="mb-0">{route.name}</h5>
              <small className="text-muted">{route.route_id}</small>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Badge bg={getStatusBadge(route.isActive)}>
              {route.isActive ? "ACTIVE" : "INACTIVE"}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted mb-3">Route Information</h6>
              <p>
                <strong>Description:</strong> {route.description || "N/A"}
              </p>
              <p>
                <FaMapMarkerAlt className="text-success me-2" />
                <strong>Origin:</strong> {route.originAddress}
              </p>
              <p>
                <FaMapMarkerAlt className="text-danger me-2" />
                <strong>Destination:</strong> {route.destinationAddress}
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-3">Route Metrics</h6>
              <p>
                <FaRoad className="text-primary me-2" />
                <strong>Distance:</strong>{" "}
                {route.distance ? `${route.distance} km` : "N/A"}
              </p>
              <p>
                <FaClock className="text-primary me-2" />
                <strong>Estimated Duration:</strong>{" "}
                {route.estimatedDuration
                  ? `${route.estimatedDuration} mins`
                  : "N/A"}
              </p>
              <p>
                <strong>Waypoints:</strong> {route.waypoints?.length || 0}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Waypoints */}
      {route.waypoints && route.waypoints.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <FaMapMarkerAlt className="me-2" />
            Waypoints ({route.waypoints.length})
          </Card.Header>
          <Card.Body>
            <div className="row">
              {route.waypoints.map((waypoint, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="border rounded p-3">
                    <div className="d-flex align-items-center mb-2">
                      <Badge bg="primary" className="me-2">
                        {index + 1}
                      </Badge>
                      <strong>{waypoint.address}</strong>
                    </div>
                    <small className="text-muted">
                      Lat: {waypoint.lat}, Lng: {waypoint.lng}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Statistics */}
      <div className="row mb-4">
        <div className="col-md-4">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{route.distance || 0}</h3>
              <small className="text-muted">Distance (km)</small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {route.estimatedDuration
                  ? Math.floor(route.estimatedDuration / 60)
                  : 0}
              </h3>
              <small className="text-muted">Duration (mins)</small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{route.trips?.length || 0}</h3>
              <small className="text-muted">Total Trips</small>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Timestamps */}
      <Card>
        <Card.Header>Route Timeline</Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <p>
                <FaCalendar className="text-primary me-2" />
                <strong>Created:</strong>{" "}
                {moment(route.createdAt).format("MMM DD, YYYY HH:mm")}
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-0">
                <FaCalendar className="text-primary me-2" />
                <strong>Last Updated:</strong>{" "}
                {moment(route.updatedAt).format("MMM DD, YYYY HH:mm")}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  // Trips Tab Content
  const renderTripsTab = () => {
    const trips = route.trips || [];

    const tripsTableHead = () => (
      <thead>
        <tr>
          <th>Trip Number</th>
          <th>Vehicle</th>
          <th>Driver</th>
          <th>Start Time</th>
          <th>End Time</th>
          <th>Distance</th>
          <th>Status</th>
          <th>Purpose</th>
        </tr>
      </thead>
    );

    const tripsDisplayTable = (trip) => (
      <>
        <td>{trip.tripNumber}</td>
        <td>{trip.vehicle_id}</td>
        <td>{trip.driver_id}</td>
        <td>{moment(trip.plannedStartTime).format("MMM DD, HH:mm")}</td>
        <td>
          {trip.plannedEndTime
            ? moment(trip.plannedEndTime).format("MMM DD, HH:mm")
            : "N/A"}
        </td>
        <td>{trip.plannedDistance || trip.actualDistance || "N/A"} km</td>
        <td>
          <Badge bg={getTripStatusBadge(trip.status)}>
            {trip.status?.replace("_", " ").toUpperCase()}
          </Badge>
        </td>
        <td>{trip.purpose}</td>
      </>
    );

    return (
      <div>
        {trips.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={tripsTableHead}
            mainDataArray={trips}
            tableDataRowFunction={tripsDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaTruck size={48} className="mb-3 opacity-50" />
              <p>No trips assigned to this route</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdropClassName="global-backdrop"
      centered
      dialogClassName="my-style-modal"
      animation={false}
      enforceFocus={false}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h4>{route.name}</h4>
            <small className="text-muted">{route.route_id}</small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="container-fluid">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "overview")}
            className="mb-3"
          >
            <Tab eventKey="overview" title="Overview">
              {renderOverviewTab()}
            </Tab>

            <Tab eventKey="trips" title={`Trips (${route.trips?.length || 0})`}>
              {renderTripsTab()}
            </Tab>
          </Tabs>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        {/* <Button
          variant="outline-primary"
          onClick={() => {
            // Handle edit action
            console.log("Edit route:", route.route_id);
          }}
        >
          <FaEdit className="me-2" />
          Edit Route
        </Button> */}
      </Modal.Footer>
    </Modal>
  );
}

export default RouteDetailsModal;
